'use client'

/**
 * The raw write handle, and the only module allowed to hold one (ADR 0012 rule 2).
 *
 * Rule 2's test is `rg -in changedoc src/ --glob '!src/document/internal/**'`, and it matches a
 * token rather than a symbol — so **nothing this module exports may contain that token**, or every
 * importer inherits the violation. The applier is `useApplyChange` and the type is `ApplyChange`
 * for that reason; the file name carries a hyphen, which the grep does not match either.
 *
 * The seam is one interface with two implementations. Implementation one is here: the change
 * function `useDocument` hands back. Implementation two is `A.change` over a document built with
 * `createTestDocument()` (`document/testing.ts`), which is what lets an operation be tested against
 * a real Automerge document with no React and no repo — the thing `CLAUDE.md`'s testing rule asks
 * for and that a stubbed handle cannot give, because a stub cannot observe a merge.
 *
 * Operations themselves never see this module. They are pure `(doc, …args) => T` functions under
 * `operations/`, and `use-document.ts` is what binds them to an applier.
 */

import { useDocument } from '@automerge/automerge-repo-react-hooks'
import { useEffect, useState } from 'react'
import type { AutomergeUrl } from '@automerge/automerge-repo'

import type { AppDocument } from '../schema'
import { getEnvironmentStorageKey } from '@/utils/environment'

/**
 * Apply a change to the document.
 *
 * The callback receives a live document inside a change transaction; it must not be held past the
 * call. A caller outside `document/` never sees one — it passes a patch to an operation instead,
 * which is what makes ADR 0012's container-identity rule enforceable rather than conventional.
 */
export type ApplyChange = (
  fn: (doc: AppDocument) => void,
  options?: { message?: string },
) => void

/**
 * The document URL this device is pointed at, kept in sync with the tab that changed it.
 *
 * **The `contour-` prefix is frozen.** `getEnvironmentStorageKey` builds the key from a former
 * product name, and renaming it orphans every existing user's document.
 */
export function useDocumentUrl(): AutomergeUrl | null {
  const [docUrl, setDocUrl] = useState<AutomergeUrl | null>(() => {
    if (typeof window === 'undefined') return null
    return localStorage.getItem(
      getEnvironmentStorageKey('automerge-doc-url'),
    ) as AutomergeUrl | null
  })

  useEffect(() => {
    const storageKey = getEnvironmentStorageKey('automerge-doc-url')

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === storageKey) {
        setDocUrl((current) => {
          const next = e.newValue as AutomergeUrl | null
          return next === current ? current : next
        })
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  return docUrl
}

/**
 * The document and an applier over it.
 *
 * `doc` is `undefined` until the handle resolves. The applier is safe to call before then and does
 * nothing, which is the behaviour every caller already relies on.
 */
export function useApplyChange(docUrl: AutomergeUrl | null): {
  doc: AppDocument | undefined
  apply: ApplyChange
  ready: boolean
} {
  const [doc, apply] = useDocument<AppDocument>(docUrl ?? undefined)

  return { doc, apply, ready: !!doc }
}
